import React, { useState, useEffect } from 'react';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { createAuthRequestInit } from '../../utils/auth';
import { API_BASE } from '../../config/api';

interface UserNotification {
  id: string;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  type?: 'success' | 'failure' | 'info';
  action?: string;
}

interface NotificationPreference {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
  frequency: 'instant' | 'daily' | 'weekly' | 'never';
}

export const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterRead, setFilterRead] = useState<'all' | 'read' | 'unread'>('all');
  const [expandedNotification, setExpandedNotification] = useState<string | null>(null);
  const [deletingNotification, setDeletingNotification] = useState<string | null>(null);

  const [preferences, setPreferences] = useState<NotificationPreference[]>([
    {
      id: 'low-balance',
      title: 'Low Balance Notifications',
      description: 'Get notified when your SMS credits are running low to ensure uninterrupted service.',
      enabled: true,
      frequency: 'instant'
    },
    {
      id: 'rate-changes',
      title: 'Rate Changes & Special Offers',
      description: 'Stay informed about pricing updates and exclusive promotional offers.',
      enabled: false,
      frequency: 'daily'
    },
    {
      id: 'reports',
      title: 'Reports & Analytics',
      description: 'Receive periodic reports about your SMS campaigns and delivery statistics.',
      enabled: true,
      frequency: 'weekly'
    },
    {
      id: 'campaign-updates',
      title: 'Campaign Updates',
      description: 'Get notified about the status of your SMS campaigns and delivery updates.',
      enabled: true,
      frequency: 'instant'
    },
    {
      id: 'system-alerts',
      title: 'System Alerts',
      description: 'Receive important system notifications and maintenance alerts.',
      enabled: true,
      frequency: 'instant'
    },
    {
      id: 'account-updates',
      title: 'Account Updates',
      description: 'Notifications about changes to your account, subscription, or billing.',
      enabled: true,
      frequency: 'daily'
    }
  ]);

  // Load notifications
  useEffect(() => {
    fetchNotifications();
    loadPreferences();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/notifications/user`, createAuthRequestInit());
      if (res.ok) {
        const json = await res.json();
        const userNotifications = json.data?.notifications || json.notifications || [];
        setNotifications(userNotifications);
      } else {
        throw new Error('Failed to load notifications');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPreferences = async () => {
    try {
      const res = await fetch(`${API_BASE}/notifications/preferences`, createAuthRequestInit());
      if (res.ok) {
        const json = await res.json();
        const userPreferences = json.data?.preferences || json.preferences || [];
        if (userPreferences.length > 0) {
          setPreferences(userPreferences);
        }
      }
    } catch (e: any) {
      console.error('Failed to load notification preferences:', e);
      // Use default preferences if API fails
    }
  };

  const toggleNotificationRead = async (notificationId: string, currentReadStatus: boolean, event?: React.MouseEvent | React.ChangeEvent<HTMLInputElement>) => {
    if (event) {
      event.stopPropagation();
    }
    
    try {
      const newReadStatus = !currentReadStatus;
      
      // Optimistic update
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: newReadStatus } : n)
      );

      // API call
      if (newReadStatus) {
        // Mark as read
        const res = await fetch(`${API_BASE}/notifications/${notificationId}/read`, createAuthRequestInit({
          method: 'POST'
        }, true));
        if (!res.ok) {
          // Revert on error
          setNotifications(prev => 
            prev.map(n => n.id === notificationId ? { ...n, read: currentReadStatus } : n)
          );
        }
      } else {
        // Mark as unread
        const res = await fetch(`${API_BASE}/notifications/${notificationId}/unread`, createAuthRequestInit({
          method: 'POST'
        }, true));
        if (!res.ok) {
          // Revert on error
          setNotifications(prev => 
            prev.map(n => n.id === notificationId ? { ...n, read: currentReadStatus } : n)
          );
        }
      }
    } catch (error) {
      console.error('Failed to toggle notification read status:', error);
      // Revert on error
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: currentReadStatus } : n)
      );
    }
  };

  const deleteNotification = async (notificationId: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    if (!window.confirm('Are you sure you want to delete this notification? This action cannot be undone.')) {
      return;
    }

    setDeletingNotification(notificationId);
    try {
      const res = await fetch(`${API_BASE}/notifications/${notificationId}`, createAuthRequestInit({
        method: 'DELETE'
      }, true));

      if (res.ok) {
        // Remove from list
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        // Close expanded view if this notification was expanded
        if (expandedNotification === notificationId) {
          setExpandedNotification(null);
        }
      } else {
        const errorData = await res.json().catch(() => ({ message: 'Failed to delete notification' }));
        throw new Error(errorData.message || 'Failed to delete notification');
      }
    } catch (error: any) {
      console.error('Failed to delete notification:', error);
      alert(error?.message || 'Failed to delete notification');
    } finally {
      setDeletingNotification(null);
    }
  };

  const handlePreferenceToggle = async (id: string) => {
    const updated = preferences.map(pref => 
      pref.id === id 
        ? { ...pref, enabled: !pref.enabled }
        : pref
    );
    setPreferences(updated);
    await savePreferences(updated);
  };

  const handleFrequencyChange = async (id: string, frequency: 'instant' | 'daily' | 'weekly' | 'never') => {
    const updated = preferences.map(pref => 
      pref.id === id 
        ? { ...pref, frequency, enabled: frequency !== 'never' }
        : pref
    );
    setPreferences(updated);
    await savePreferences(updated);
  };

  const savePreferences = async (prefsToSave?: NotificationPreference[]) => {
    try {
      const prefs = prefsToSave || preferences;
      const res = await fetch(`${API_BASE}/notifications/preferences`, createAuthRequestInit({
        method: 'POST',
        body: JSON.stringify({ preferences: prefs })
      }, true));
      
      if (!res.ok) {
        throw new Error('Failed to save preferences');
      }
    } catch (e: any) {
      console.error('Failed to save notification preferences:', e);
      // Revert on error
      await loadPreferences();
    }
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filterRead === 'read') return notif.read;
    if (filterRead === 'unread') return !notif.read;
    return true;
  });

  const getStatusColor = (type?: string) => {
    switch (type) {
      case 'success': return 'bg-green-500';
      case 'failure': return 'bg-red-500';
      default: return 'bg-blue-500';
    }
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'instant': return 'Instant';
      case 'daily': return 'Daily Digest';
      case 'weekly': return 'Weekly Digest';
      case 'never': return 'Never';
      default: return frequency;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white border-b">
        <div className="px-6 py-4">
          <h1 className="text-xl font-bold">Notifications</h1>
          <p className="mt-1 text-sm text-gray-600">Manage your notifications and preferences</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 space-y-6">
        {/* Section 1: Notifications List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">All Notifications</h2>
              <div className="flex items-center space-x-4">
                <select
                  value={filterRead}
                  onChange={(e) => setFilterRead(e.target.value as 'all' | 'read' | 'unread')}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                >
                  <option value="all">All</option>
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                </select>
                <button
                  onClick={fetchNotifications}
                  className="text-sm text-[#FF5722] hover:text-[#F4511E] font-medium"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {loading ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">Loading notifications...</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-lg mb-4">
                  <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                </div>
                <p className="text-sm text-gray-600">No notifications found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredNotifications.map((notification) => (
                  <div key={notification.id}>
                    {/* Notification Card */}
                    <div
                      onClick={() => setExpandedNotification(expandedNotification === notification.id ? null : notification.id)}
                      className={`flex items-start space-x-3 p-4 rounded-lg border transition-colors cursor-pointer ${
                        !notification.read 
                          ? 'bg-blue-50 border-blue-200' 
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      } ${expandedNotification === notification.id ? 'ring-2 ring-[#FF5722]' : ''}`}
                    >
                      <div className="flex-shrink-0 mt-1" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={notification.read}
                          onChange={(e) => toggleNotificationRead(notification.id, notification.read, e)}
                          className="h-4 w-4 rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722] cursor-pointer"
                        />
                      </div>
                      <div className="flex-shrink-0 mt-1">
                        <div className={`w-2 h-2 rounded-full ${getStatusColor(notification.type)}`}></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <p className={`text-sm font-medium ${
                                !notification.read ? 'text-gray-900' : 'text-gray-700'
                              }`}>
                                {notification.title}
                              </p>
                              {!notification.read && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-[#FF5722] text-white rounded-full">
                                  New
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {notification.message}
                            </p>
                            {notification.action && (
                              <p className="text-xs text-gray-500 mt-1">
                                Action: {notification.action}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 mt-2">
                              {new Date(notification.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Notification View */}
                    {expandedNotification === notification.id && (
                      <div className="mt-2 p-6 bg-white border border-gray-200 rounded-lg shadow-lg">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className={`w-3 h-3 rounded-full ${getStatusColor(notification.type)}`}></div>
                              <h3 className="text-lg font-semibold text-gray-900">{notification.title}</h3>
                              {!notification.read && (
                                <span className="px-2 py-1 text-xs font-medium bg-[#FF5722] text-white rounded-full">
                                  New
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mb-1">
                              {new Date(notification.createdAt).toLocaleString()}
                            </p>
                          </div>
                          <button
                            onClick={() => setExpandedNotification(null)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <XMarkIcon className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="mb-6">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {notification.message}
                          </p>
                          {notification.action && (
                            <div className="mt-4 p-3 bg-gray-50 rounded-md">
                              <p className="text-xs font-medium text-gray-500 mb-1">Action Triggered</p>
                              <p className="text-sm text-gray-700">{notification.action}</p>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                          <div className="flex items-center space-x-3">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={notification.read}
                                onChange={(e) => toggleNotificationRead(notification.id, notification.read, e)}
                                className="h-4 w-4 rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                              />
                              <span className="text-sm text-gray-700">
                                {notification.read ? 'Mark as unread' : 'Mark as read'}
                              </span>
                            </label>
                          </div>
                          <button
                            onClick={(e) => deleteNotification(notification.id, e)}
                            disabled={deletingNotification === notification.id}
                            className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                          >
                            <TrashIcon className="w-5 h-5" />
                            <span>{deletingNotification === notification.id ? 'Deleting...' : 'Delete'}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section 2: Notification Settings */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>
            <p className="mt-1 text-sm text-gray-600">
              Choose which notifications you want to receive and how often
            </p>
          </div>

          <div className="p-6 space-y-6">
            {preferences.map((preference) => (
              <div 
                key={preference.id}
                className="flex items-start justify-between py-4 border-b last:border-b-0"
              >
                <div className="flex-1 pr-6">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-base font-medium text-gray-900">
                      {preference.title}
                  </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      preference.enabled 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {preference.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-3">
                    {preference.description}
                  </p>
                  
                  {preference.enabled && (
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Notification Frequency
                      </label>
                      <select
                        value={preference.frequency}
                        onChange={(e) => handleFrequencyChange(preference.id, e.target.value as 'instant' | 'daily' | 'weekly' | 'never')}
                        className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                      >
                        <option value="instant">Instant</option>
                        <option value="daily">Daily Digest</option>
                        <option value="weekly">Weekly Digest</option>
                        <option value="never">Never</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        Current: {getFrequencyLabel(preference.frequency)}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex items-center">
                  <button
                    onClick={() => handlePreferenceToggle(preference.id)}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:ring-offset-2
                      ${preference.enabled ? 'bg-[#FF5722]' : 'bg-gray-200'}
                    `}
                    role="switch"
                    aria-checked={preference.enabled}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                        transition duration-200 ease-in-out
                        ${preference.enabled ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>
              </div>
            ))}
        </div>

          {/* Info Note */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-start">
            <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-gray-900">
                  About Notification Preferences
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                  These settings control how and when you receive notifications. 
                  Instant notifications are sent immediately, while digest options bundle multiple notifications together.
                  Email notification preferences can be managed separately in your account settings.
              </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage; 
